'use client'

import React, { useCallback, useMemo } from 'react'

import { compact, uniqBy } from 'lodash'
import { CheckCircle2, PlugIcon } from 'lucide-react'
import Image from 'next/image'
import { Hex, hexToString } from 'viem'

import { CopyCurrentUrlButton } from '~/components/CopyCurrentUrlButton'
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  EmptyState,
  NetworkDisplay,
} from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { Timestamp } from '~/components/component-library/Timestamp'
import { TransactionDialogLoadingContent } from '~/components/smart-accounts/TransactionDialogLoadingContent'
import { HistoryItem } from '~/components/smart-accounts/utils'
import { SupportedChainId } from '~/constants/chains'
import { useWalletConnectContext } from '~/context/WalletConnectProvider'
import { useApproveMessage } from '~/hooks/smart-accounts/useMessageActions'
import {
  useMessage,
  useMessageProperties,
} from '~/hooks/smart-accounts/useMessages'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useSmartAccount, useSmartAccounts } from '~/hooks/useSmartAccounts'
import { useDialogRoutePath } from '~/hooks/utils'
import { IAddress, OrgMessage, OrgSmartAccount } from '~/types'
import { shortenAddress } from '~/utils/addresses'
import {
  appendEmailToAuthenticatorName,
  getMessageSignatureCount,
} from '~/utils/smartAccounts'

const DEFAULT_WALLET_CONNECT_APP_ICON = (
  <div className="h-14 w-14 rounded-lg bg-gray-500" />
)

export const ApproveMessageDialogContent = ({
  messageId,
  onClose,
  globalModal,
}: {
  messageId: string
  onClose: () => void
  globalModal?: boolean
}) => {
  const { message, isLoading } = useMessage(messageId)
  const { smartAccounts } = useSmartAccounts()
  const smartAccount = smartAccounts.find(
    (sa) => sa.id === message?.smartAccountId,
  )

  useDialogRoutePath({
    globalModal,
    closeModal: onClose,
    getPath: () => {
      if (message && smartAccount) {
        return `/accounts/${smartAccount.address}/messages/${message.id}/`
      }
    },
  })

  const isTransactionFound = smartAccount && message
  const isActionsDisabled = isLoading || !isTransactionFound

  return (
    <SplitsDialogContent
      id="approveMessage"
      title="Review message"
      variant="cta"
      actionButton={{
        element: (
          <ApproveMessageButton messageId={messageId} onClose={onClose} />
        ),
      }}
    >
      {isLoading ? (
        <TransactionDialogLoadingContent />
      ) : !isTransactionFound ? (
        <EmptyState title="Transaction not found" />
      ) : (
        <div className="space-y-8">
          <MessageHeader message={message} />
          <MessageHistory message={message} smartAccount={smartAccount} />
          <MessageDetails message={message} from={smartAccount.address} />
        </div>
      )}
      <CopyCurrentUrlButton
        isDisabled={isActionsDisabled}
        trackingId={`message.${messageId}`}
      />
    </SplitsDialogContent>
  )
}

export const SubmitMessageButton = ({
  messageId,
  onClose,
}: {
  messageId: string
  onClose?: () => void
}) => {
  const { message, isLoading } = useMessage(messageId)

  const { canSubmit } = useMessageProperties(messageId)
  const { respondWithSignature } = useWalletConnectContext()

  const onSubmit: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.stopPropagation()
    if (message) {
      await respondWithSignature(message)
      onClose?.()
    }
  }

  if (isLoading) {
    return (
      <Button isDisabled disabledReason={'Loading message'} fullWidth>
        Submit
      </Button>
    )
  }

  const isDisabled = !canSubmit
  return (
    <Button
      onClick={onSubmit}
      eventName={'clickedSubmitMessage'}
      eventProperties={{ messageId: message!.id }}
      fullWidth
      isDisabled={isDisabled}
    >
      Submit
    </Button>
  )
}

export const ApproveMessageButton = ({
  messageId,
  onClose,
}: {
  messageId: string
  onClose?: () => void
}) => {
  const { message, isLoading } = useMessage(messageId)

  const { approveMessage, isProcessing } = useApproveMessage()

  const { canSign, hasSigned } = useMessageProperties(messageId)

  const onSubmit: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async (e) => {
      e.stopPropagation()
      if (message) {
        approveMessage(message, true, () => {
          onClose?.()
        })
      }
    },
    [message, approveMessage, onClose],
  )

  if (isLoading) {
    return (
      <Button isDisabled disabledReason={'Loading message'} fullWidth>
        Sign
      </Button>
    )
  }

  const isDisabled = !canSign
  return (
    <Button
      shortcut={{
        key: 'meta+enter',
        callback: (e) =>
          onSubmit(e as unknown as React.MouseEvent<HTMLButtonElement>),
      }}
      onClick={onSubmit}
      eventName={'clickedSignMessage'}
      eventProperties={{ messageId: message!.id }}
      isLoading={isProcessing}
      disabledReason={
        isDisabled && hasSigned
          ? 'You have already signed this message'
          : undefined
      }
      fullWidth
      isDisabled={isDisabled}
    >
      Sign
    </Button>
  )
}

export const MessageHeader = ({ message }: { message: OrgMessage }) => {
  const { smartAccount } = useSmartAccount(message?.smartAccountAddress)
  const isApproved =
    smartAccount && getMessageSignatureCount(message) >= smartAccount.threshold

  const isWalletConnectMessage =
    message.walletConnectAppName ||
    message.walletConnectAppIcon ||
    message.walletConnectSessionRequestId ||
    message.walletConnectSessionRequestTopic

  return (
    <div className="space-y-5">
      <div className="flex justify-center">
        {isApproved ? (
          <CheckCircle2 className="h-14 w-14 text-green-500" />
        ) : (
          <div className={'flex h-14 w-14 items-center rounded-lg p-1'}>
            {isWalletConnectMessage ? (
              <Image
                src={
                  message.walletConnectAppIcon ??
                  '/wallets/walletconnect-logo.svg'
                }
                alt={message.walletConnectAppName ?? 'wallet connect app'}
                width={56}
                height={56}
              />
            ) : (
              <PlugIcon size={56} />
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col items-center">
        <h2 className="flex-grow text-wrap text-lg font-medium">
          {message.name}
        </h2>
      </div>
    </div>
  )
}

export const MessageHistory = ({
  message,
  smartAccount,
}: {
  message: OrgMessage
  smartAccount: OrgSmartAccount
}) => {
  const { user: authUser } = useAuthUser()

  const signaturesNeeded =
    smartAccount.threshold - getMessageSignatureCount(message)
  const isApproved = signaturesNeeded <= 0
  const remainingSignerUsers = uniqBy(
    compact(
      smartAccount.signers
        .filter((accountSigner) => {
          const hasAlreadySigned = message.signers.some(
            (messageSigner) => messageSigner.id === accountSigner.id,
          )
          return !hasAlreadySigned
        })
        .map((signer) => signer.user),
    ),
    'id',
  )

  const items = compact([
    {
      title: `Created ${
        !message.createdByUserId
          ? ''
          : message.createdByUserId === authUser?.id
            ? `by you (${message.createdByUserEmail})`
            : `by ${message.createdByUserEmail}`
      }`,
      date: new Date(message.createdAt),
    },
    ...message.signers.map((signer) => ({
      title: `Signed ${
        signer.user
          ? `by ${
              signer.user.id === authUser?.id
                ? `you (${signer.name})`
                : appendEmailToAuthenticatorName(
                    signer.name || '',
                    signer.user.email,
                  )
            }`
          : ''
      }`,
      date: new Date(signer.signedAt),
    })),
    ...message.eoaSigners.map((signer) => ({
      title: `Signed by ${signer.name ?? shortenAddress(signer.address)}`,
      date: new Date(signer.signedAt),
    })),
    !isApproved
      ? {
          title: `${signaturesNeeded} signature${
            signaturesNeeded > 1 ? 's' : ''
          } needed`,
          subtitle: (
            <div className="space-y-1">
              {remainingSignerUsers.map((user, index) => (
                <div key={index}>
                  {user.id === authUser?.id
                    ? `You (${user.email})`
                    : user.email}
                </div>
              ))}
            </div>
          ),
        }
      : null,
  ])

  return (
    <div className="relative overflow-hidden rounded-xl bg-gray-50 px-8 py-6 dark:bg-gray-950">
      {items.map((item, index) => (
        <HistoryItem
          key={index}
          title={item.title}
          subtitle={
            item.date ? (
              <Timestamp
                timestamp={item.date.getTime()}
                type="absolute"
                abbreviate
                showTooltip
              />
            ) : (
              ''
            )
          }
          completed={item.date !== undefined}
          first={index === 0}
          last={index === items.length - 1}
        />
      ))}
    </div>
  )
}

export const MessageDetails = ({
  message,
  from,
}: {
  message: OrgMessage
  from: IAddress
}) => {
  return (
    <div className="divide-y divide-muted">
      <div className="flex flex-col space-y-1 py-4">
        <div className="flex justify-between">
          <div className="text-secondary">From</div>
          <div className="col-span-2">
            <AddressDisplay
              address={from}
              chainId={message.chainId}
              layout="standard"
              isInternal
            />
          </div>
        </div>
        <div className="flex justify-between">
          <div className="text-secondary">Network</div>
          <div className="col-span-2">
            <NetworkDisplay
              layout="standard"
              chainId={message.chainId as SupportedChainId}
            />
          </div>
        </div>
        <div className="text-secondary">Message</div>
        {message.dataType === 'PERSONAL_SIGN' ? (
          <MessageTextWindow message={message.message} />
        ) : message.dataType === 'ETH_SIGNTYPEDDATA_V4' ? (
          <TypedDataTextWindow typedData={message.message} />
        ) : (
          <div>Unsupported data type: {message.dataType}</div>
        )}
      </div>
    </div>
  )
}

export const MessageDetailDialogContent = ({
  messageId,
  onClose,
  globalModal,
}: {
  messageId: string
  onClose: () => void
  globalModal?: boolean
}) => {
  const { message, isLoading } = useMessage(messageId)
  const { smartAccounts } = useSmartAccounts()
  const smartAccount = smartAccounts.find(
    (sa) => sa.id === message?.smartAccountId,
  )
  const isActionsDisabled = isLoading || !message
  const { canSubmit } = useMessageProperties(messageId)

  useDialogRoutePath({
    globalModal,
    closeModal: onClose,
    getPath: () => {
      if (message && smartAccount) {
        return `/accounts/${smartAccount.address}/messages/${message.id}/`
      }
    },
  })

  return (
    <SplitsDialogContent
      id="messageDetailsDialog"
      title={'Message details'}
      variant="cta"
      actionButton={{
        element: canSubmit ? (
          <SubmitMessageButton messageId={messageId} onClose={onClose} />
        ) : (
          <></>
        ),
      }}
    >
      {isLoading ? (
        <TransactionDialogLoadingContent />
      ) : !message || !smartAccount ? (
        <EmptyState title="Message not found" />
      ) : (
        <>
          <MessageHeader message={message} />
          <MessageHistory message={message} smartAccount={smartAccount} />
          <MessageDetails message={message} from={smartAccount.address} />
        </>
      )}
      <CopyCurrentUrlButton
        isDisabled={isActionsDisabled}
        trackingId={`message.${messageId}`}
      />
    </SplitsDialogContent>
  )
}

export const MessageHeaderPreview = ({
  name,
  icon,
}: {
  name: string
  icon?: React.ReactNode
}) => {
  return (
    <div className="space-y-5">
      <div className="flex justify-center">
        {icon ?? DEFAULT_WALLET_CONNECT_APP_ICON}
      </div>
      <div className="flex flex-col items-center">
        <h2 className="flex-grow text-wrap text-lg font-medium">{name}</h2>
      </div>
    </div>
  )
}

export const MessageDetailsPreview = ({
  chainId,
  smartAccountAddress,
  dataType,
  dataToSign,
}: {
  chainId: SupportedChainId
  smartAccountAddress: IAddress
  dataType: 'PERSONAL_SIGN' | 'ETH_SIGNTYPEDDATA_V4'
  dataToSign: Hex
}) => {
  return (
    <div className="divide-y divide-muted">
      <div className="flex flex-col space-y-1 py-2">
        <div className="flex justify-between">
          <div className="text-secondary">Account</div>
          <div className="col-span-2">
            <AddressDisplay
              address={smartAccountAddress}
              chainId={chainId}
              layout="standard"
              isInternal
            />
          </div>
        </div>
        <div className="flex justify-between">
          <div className="text-secondary">Network</div>
          <div className="col-span-2">
            <NetworkDisplay layout="standard" chainId={chainId} />
          </div>
        </div>
      </div>
      <div className="flex flex-col space-y-1 py-2">
        <Collapsible defaultOpen className="space-y-2">
          <CollapsibleTrigger className="flex w-full justify-between text-secondary">
            Message
          </CollapsibleTrigger>
          <CollapsibleContent>
            {dataType === 'PERSONAL_SIGN' ? (
              <MessageTextWindow message={dataToSign} />
            ) : dataType === 'ETH_SIGNTYPEDDATA_V4' ? (
              <TypedDataTextWindow typedData={dataToSign} />
            ) : (
              <div>Unsupported data type: {dataType}</div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  )
}

const MessageTextWindow = ({ message }: { message: Hex }) => {
  const stringMessage = hexToString(message)

  return (
    <div className="w-full break-words rounded border border-default bg-gray-50 p-4 dark:bg-gray-900">
      {stringMessage.split('\n').map((line, index) => {
        return (
          <div key={`sign-message-line-${index}`}>
            {line}
            <br />
          </div>
        )
      })}
    </div>
  )
}

const TypedDataTextWindow = ({ typedData }: { typedData: string }) => {
  const jsonTypedData = useMemo(() => {
    return JSON.parse(typedData)
  }, [typedData])

  const title = jsonTypedData.primaryType
  const data = jsonTypedData.message

  return (
    <div className="w-full break-words rounded border border-default bg-gray-50 p-4 dark:bg-gray-900">
      <div>{title}</div>
      <DisplayTypedData data={data} />
    </div>
  )
}

const DisplayTypedData = ({
  data,
  indentLevel = 0,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: { [key: string]: any }
  indentLevel?: number
}) => {
  return (
    <>
      {Object.keys(data).map((dataKey, index) => {
        const value = data[dataKey]
        let displayValue = value
        if (typeof value === 'object') {
          if (Array.isArray(value)) displayValue = value.join(', ')
          else
            return (
              <>
                <span>{dataKey}:</span>
                <DisplayTypedData data={value} indentLevel={indentLevel + 1} />
              </>
            )
        }

        return (
          <div
            style={{ marginLeft: `${indentLevel * 20}px` }}
            key={`sign-data-line-${dataKey}-${index}`}
          >
            {dataKey}: {displayValue.toString()}
          </div>
        )
      })}
    </>
  )
}
